import * as React from "react"
import { cn } from "@/lib/utils"

interface PillProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
  label: string
  state?: "idle" | "active"
  tone?: "accent"
  /** "pill" = rounded outlined/filled pill; "button" = filled rect primary action */
  variant?: "pill" | "button"
  disabled?: boolean
}

export const Pill = React.forwardRef<HTMLButtonElement, PillProps>(function Pill(
  { label, state = "idle", tone = "accent", variant = "pill", disabled = false, className, ...rest },
  ref,
) {
  const isActive = state === "active"

  if (variant === "button") {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        {...rest}
        className={cn(
          "inline-flex items-center justify-center whitespace-nowrap",
          "h-[30px] px-4 rounded-[var(--radius-md)]",
          "text-[length:var(--text-sm)] font-semibold",
          "transition-colors duration-[var(--duration-fast)]",
          "disabled:opacity-40 disabled:pointer-events-none",
          "bg-[var(--lyt-accent)] text-[color:var(--text-on-accent-dark)] border-0",
          "hover:bg-[var(--lyt-accent-hover)] active:bg-[var(--lyt-accent-active)]",
          className
        )}
      >
        {label}
      </button>
    )
  }

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      {...rest}
      className={cn(
        "inline-flex items-center justify-center rounded-[var(--radius-full)]",
        "h-7 px-3 text-xs font-medium whitespace-nowrap",
        "transition-colors duration-[var(--duration-fast)]",
        "disabled:opacity-40 disabled:pointer-events-none",
        tone === "accent" && !isActive && [
          "border border-[color:var(--lyt-accent)] text-[color:var(--lyt-accent)]",
          "hover:bg-[var(--lyt-accent-dim)]",
        ],
        tone === "accent" && isActive && [
          "bg-[color:var(--lyt-accent)] text-[color:var(--lyt-accent-on)]",
          "hover:opacity-90",
        ],
        className
      )}
    >
      {label}
    </button>
  )
})
